import { useQuery } from '@apollo/client'
import moment from 'moment'
import React from 'react'
import { Badge, Button, Form, Spinner } from 'react-bootstrap'
import { GET_CONTACT_COMMUNICATION_LOGS } from '../../../gql/queries/contactsApiQueries'
import { useDisclourse } from '../../../hooks/useDisclourse'
import { SendMessageCanvas } from '../../../components/messaging/SendMessageCanvas'

interface ICommunicationsTabProps {
  originModule: 'quotes' | 'policies' | 'leads'
  originId: string
  contactId?: string
  recipient?: {
    name: string
    phone: string
    email?: string
  }
  contextData?: any
}

export const CommunicationsTab: React.FC<ICommunicationsTabProps> = ({
  originModule,
  originId,
  contactId,
  recipient,
  contextData,
}) => {
  const { isOpen: isOpenMessaging, onOpen: onOpenMessaging, onClose: onCloseMessaging } = useDisclourse()
  const historyContainerRef = React.useRef<HTMLDivElement | null>(null)

  const normalizedOriginModule = React.useMemo(() => {
    switch (originModule) {
      case 'policies':
      case 'quotes':
        // Show both Policy and Quote history combined
        return 'policy,quote'
      case 'leads':
        return 'lead'
      default:
        return originModule
    }
  }, [originModule])

  const {
    data: objectData,
    loading: loadingObjectLogs,
    error: objectError,
    refetch: refetchObjectLogs,
  } = useQuery(GET_CONTACT_COMMUNICATION_LOGS, {
    variables: {
      originModule: normalizedOriginModule,
      originId,
    },
    skip: !originId,
    fetchPolicy: 'network-only',
  })

  const {
    data: contactData,
    loading: loadingContactLogs,
    error: contactError,
    refetch: refetchContactLogs,
  } = useQuery(GET_CONTACT_COMMUNICATION_LOGS, {
    variables: { contactId },
    skip: !contactId,
    fetchPolicy: 'network-only',
  })

  const [selectedChannels, setSelectedChannels] = React.useState<string[]>(['WHATSAPP', 'SMS', 'EMAIL'])
  const logs = React.useMemo(() => {
    const merged = new Map<string, any>()
    const allEdges = [
      ...(objectData?.allCommunications?.edges || []),
      ...(contactData?.allCommunications?.edges || []),
    ]

    allEdges.forEach((edge: any) => {
      if (!edge?.node?.id || merged.has(edge.node.id)) return
      merged.set(edge.node.id, edge)
    })

    return Array.from(merged.values())
  }, [objectData, contactData])

  const loading = loadingObjectLogs || loadingContactLogs
  const error = objectError || contactError

  const refetch = React.useCallback(async () => {
    await Promise.all([
      refetchObjectLogs(),
      contactId ? refetchContactLogs() : Promise.resolve(),
    ])
  }, [contactId, refetchContactLogs, refetchObjectLogs])

  const filteredAndSortedLogs = React.useMemo(() => {
    return [...logs]
      .filter(({ node }: any) => {
        const channel = node.channel?.toUpperCase() || 'OTHER'
        return selectedChannels.includes(channel)
      })
      .sort((a: any, b: any) => {
        return new Date(a.node.created).getTime() - new Date(b.node.created).getTime()
      })
  }, [logs, selectedChannels])

  React.useEffect(() => {
    const container = historyContainerRef.current
    if (!container || filteredAndSortedLogs.length === 0) return
    container.scrollTop = container.scrollHeight
  }, [filteredAndSortedLogs])

  const toggleChannel = (channel: string) => {
    if (selectedChannels.includes(channel)) {
      setSelectedChannels(selectedChannels.filter((c) => c !== channel))
    } else {
      setSelectedChannels([...selectedChannels, channel])
    }
  }

  const getStatusBadge = (status: string) => {
    const s = status.toUpperCase()
    if (s === 'SENT') return 'primary'
    if (s === 'DELIVERED') return 'info'
    if (s === 'READ') return 'success'
    if (s === 'FAILED') return 'danger'
    if (s === 'RECEIVED') return 'warning'
    return 'secondary'
  }

  const getChannelIcon = (channel: string) => {
    const c = channel.toUpperCase()
    if (c === 'WHATSAPP') return 'bi-whatsapp'
    if (c === 'SMS') return 'bi-chat-dots'
    if (c === 'EMAIL') return 'bi-envelope'
    return 'bi-chat'
  }

  if (loading) {
    return (
      <div className='d-flex flex-column align-items-center justify-content-center p-10'>
        <Spinner animation='border' variant='primary' />
        <span className='mt-2 text-muted'>Loading communications...</span>
      </div>
    )
  }

  if (error) {
    return (
      <div className='alert alert-danger'>
        <i className='bi bi-exclamation-triangle-fill me-2'></i>
        Error loading communications: {error.message}
      </div>
    )
  }

  return (
    <div className='bg-white p-6 rounded border'>
      <div className='d-flex flex-wrap justify-content-between align-items-center mb-6 gap-3'>
        <div className='d-flex align-items-center gap-4'>
          <h3 className='m-0 me-2'>Communication History</h3>
          <div className='d-flex align-items-center gap-3 bg-light px-4 py-2 rounded-pill border'>
            <span className='text-muted fw-bold small text-uppercase me-1'>Filter:</span>
            <Form.Check
              type='checkbox'
              id='filter-whatsapp'
              label={<><i className='bi bi-whatsapp success ms-1'></i> WhatsApp</>}
              checked={selectedChannels.includes('WHATSAPP')}
              onChange={() => toggleChannel('WHATSAPP')}
              className='small fw-bold'
            />
            <Form.Check
              type='checkbox'
              id='filter-sms'
              label={<><i className='bi bi-chat-dots ms-1 text-primary'></i> SMS</>}
              checked={selectedChannels.includes('SMS')}
              onChange={() => toggleChannel('SMS')}
              className='small fw-bold'
            />
          </div>
        </div>
        <div className='d-flex gap-2'>
          <Button variant='outline-secondary' size='sm' onClick={() => refetch()} title="Refresh History">
            <i className='bi bi-arrow-clockwise'></i>
          </Button>
          {recipient && (
            <Button variant='primary' size='sm' onClick={onOpenMessaging}>
              <i className='bi bi-send me-2'></i>
              Send Message
            </Button>
          )}
        </div>
      </div>

      {logs.length === 0 ? (
        <div className='text-center p-10 py-15'>
          <i className='bi bi-chat-left-text text-muted fs-1 mb-3 d-block'></i>
          <p className='text-muted'>No communication logs found.</p>
        </div>
      ) : (
        <div
          ref={historyContainerRef}
          className='d-flex flex-column gap-4 pe-2'
          style={{
            maxHeight: '70vh',
            minHeight: '28rem',
            overflowY: 'auto',
            overflowX: 'hidden',
            scrollBehavior: 'smooth',
          }}
        >
          {filteredAndSortedLogs.map(({ node }: any) => {
            const isOutbound = node.direction === 'OUTBOUND'
            return (
              <div
                key={node.id}
                className={`d-flex flex-column ${isOutbound ? 'align-items-end' : 'align-items-start'}`}
              >
                <div
                  className={`cardbox-message`}
                  style={{
                    maxWidth: '80%',
                    backgroundColor: node.channel?.toUpperCase() === 'WHATSAPP'
                      ? (isOutbound ? '#DCF8C6' : '#E7F3EF')
                      : (isOutbound ? '#F2F2F2' : '#FFFFFF'),
                    borderColor: node.channel?.toUpperCase() === 'WHATSAPP'
                      ? (isOutbound ? '#C7E9A0' : '#C1D6D0')
                      : (isOutbound ? '#D1D1D1' : '#E0E0E0'),
                    borderBottomRightRadius: isOutbound ? '0' : '8px',
                    borderBottomLeftRadius: isOutbound ? '8px' : '0',
                  }}
                >
                  <div className='d-flex align-items-center gap-2 mb-2'>
                    <i className={`bi ${getChannelIcon(node.channel)} ${node.channel?.toUpperCase() === 'WHATSAPP' ? 'text-success' : 'text-primary'}`} />
                    <span className='fw-bold x-small text-uppercase'>{node.channel}</span>
                    <Badge bg={getStatusBadge(node.status)} className='x-small px-2' style={{ fontSize: '0.65rem' }}>
                      {node.status}
                    </Badge>
                  </div>

                  <div className='small text-break' style={{ whiteSpace: 'pre-wrap' }}>
                    {node.content || <span className='fst-italic text-muted'>No content</span>}
                  </div>

                  {node.metadata?.media_urls && Array.isArray(node.metadata.media_urls) && node.metadata.media_urls.length > 0 && (
                    <div className='mt-2 d-flex flex-column gap-1'>
                      {node.metadata.media_urls.map((url: string, idx: number) => {
                        const isImage = /\.(jpg|jpeg|png|gif|webp)$/i.test(url)
                        return (
                          <div key={idx} className='border rounded p-2 bg-white-50 d-inline-block' style={{ maxWidth: 'fit-content' }}>
                            {isImage ? (
                              <a href={url} target='_blank' rel='noopener noreferrer'>
                                <img src={url} alt="Attachment" style={{ maxHeight: '150px', maxWidth: '100%', cursor: 'pointer' }} className='rounded shadow-sm' />
                              </a>
                            ) : (
                              <a href={url} target='_blank' rel='noopener noreferrer' className='d-flex align-items-center gap-2 text-decoration-none'>
                                <i className='bi bi-file-earmark-arrow-down fs-4'></i>
                                <span className='small'>Attachment {idx + 1}</span>
                              </a>
                            )}
                          </div>
                        )
                      })}
                    </div>
                  )}

                  <div className='d-flex justify-content-end align-items-center gap-2 mt-2 pt-1 border-top border-black-50 opacity-50'>
                    <span className='x-small' style={{ fontSize: '0.7rem' }}>
                      {moment(node.created).format('hh:mm A')}
                    </span>
                    {isOutbound && (
                      <i className={`bi bi-check2-all x-small ${node.status === 'READ' ? 'text-primary' : ''}`}></i>
                    )}
                  </div>
                </div>

                <div className='d-flex gap-2 px-1' style={{ fontSize: '0.7rem' }}>
                  <span className='text-muted'>
                    {moment(node.created).format('MMM DD')}
                  </span>
                  {node.user && isOutbound && (
                    <span className='text-muted'>
                      • {node.user.firstName} {node.user.lastName}
                    </span>
                  )}
                </div>
              </div>
            )
          })}
        </div>
      )}

      {recipient && (
        <SendMessageCanvas
          show={isOpenMessaging}
          onHide={onCloseMessaging}
          recipient={recipient}
          context={{
            type: originModule === 'leads' ? 'LEAD' : originModule === 'policies' ? 'POLICY' : 'QUOTE',
            id: originId,
          }}
          data={contextData}
          defaultChannel='WHATSAPP'
          onSuccess={() => refetch()}
        />
      )}
    </div>
  )
}
